import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQueryWithReauth';
import { getAuthBaseUrl } from './api';

export const authApiSlice = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithReauth(getAuthBaseUrl()),
  tagTypes: ['User', 'UserGroup'],
  endpoints: (builder) => ({
    fetchUsers: builder.query({
      query: () => '/users/',
      providesTags: ['User'],
    }),
    createUser: builder.mutation({
      query: (userData) => {
        // Handle FormData case (with profile_picture)
        if (userData instanceof FormData) {
          // For FormData we need special handling
          // Create a new FormData object to modify
          const modifiedFormData = new FormData();

          // Copy all entries except 'groups' to maintain the same structure
          for (let [key, value] of userData.entries()) {
            if (key !== 'groups') {
              modifiedFormData.append(key, value);
            }
          }

          // Log what we're sending
          console.log('Sending user creation FormData (modified)');

          return {
            url: '/users/',
            method: 'POST',
            body: modifiedFormData,
            formData: true,
          };
        }

        // For regular JSON, we need to handle the backend behavior
        const cleanedData = { ...userData };

        // IMPORTANT: Remove the 'groups' field entirely
        // The Django backend extracts it before validation
        delete cleanedData.groups;

        console.log('Final data sent to backend (without groups):', cleanedData);

        return {
          url: '/users/',
          method: 'POST',
          body: cleanedData,
        };
      },
      invalidatesTags: ['User'],
    }),
    updateUser: builder.mutation({
      query: ({ userId, ...userData }) => ({
        url: `/users/${userId}/`,
        method: 'PUT',
        body: userData,
      }),
      invalidatesTags: ['User'],
    }),
    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `/users/${userId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
    fetchUserGroups: builder.query({
      query: () => '/groups/',
      providesTags: ['UserGroup'],
    }),
    createUserGroup: builder.mutation({
      query: (groupData) => ({
        url: '/groups/',
        method: 'POST',
        body: groupData,
      }),
      invalidatesTags: ['UserGroup'],
    }),
    updateUserGroup: builder.mutation({
      query: ({ groupId, ...groupData }) => ({
        url: `/groups/${groupId}/`,
        method: 'PUT',
        body: groupData,
      }),
      invalidatesTags: ['UserGroup'],
    }),
    deleteUserGroup: builder.mutation({
      query: (groupId) => ({
        url: `/groups/${groupId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserGroup'],
    }),
    fetchPermissions: builder.query({
      query: () => '/permissions/',
      providesTags: ['Permission'],
    }),
    createUserWithGroups: builder.mutation({
      query: (userData) => {
        // Make a copy of the user data
        const cleanedData = { ...userData };

        // Extract the groups array (don't remove it)
        const groupIds = cleanedData.groups || [];

        // Add a special flag to tell the backend this is a special case
        cleanedData._createWithGroups = true;
        cleanedData._groupIds = groupIds;

        // Keep the 'groups' field - the backend now expects it

        console.log('Creating user with special group handling:', cleanedData);

        return {
          url: '/users/create-with-groups/',
          method: 'POST',
          body: cleanedData,
        };
      },
      invalidatesTags: ['User'],
    }),

    // Add a specific password reset mutation
    setPassword: builder.mutation({
      query: ({ userId, password }) => ({
        url: `/users/${userId}/set_password/`,
        method: 'POST',
        body: { password },
      }),
      invalidatesTags: ['User'],
    }),

    // Resend welcome email with new temporary password
    resendWelcomeEmail: builder.mutation({
      query: ({ userId, force = false }) => ({
        url: `/users/${userId}/resend-welcome-email/`,
        method: 'POST',
        body: { force },
      }),
      invalidatesTags: ['User'],
    }),
  }),
});

export const {
  useFetchUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useFetchUserGroupsQuery,
  useCreateUserGroupMutation,
  useUpdateUserGroupMutation,
  useDeleteUserGroupMutation,
  useFetchPermissionsQuery,
  useCreateUserWithGroupsMutation,
  useSetPasswordMutation,
  useResendWelcomeEmailMutation,
} = authApiSlice;
